import { MetricOptionMenuItem } from "./MetricOptionText";
import WidgetRenderer from "./charts/WidgetRenderer";
import React, { useState, useEffect } from "react";
import InputLabel from "@mui/material/InputLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { formatDate, dateStrToObj } from "../requests/utilities/helpers";
import {
  API_calculate_metric,
  API_get_company_metrics,
  API_get_event_metrics,
  API_save_report,
  API_get_saved_reports,
  API_delete_saved_report,
  API_update_saved_reports_order,
  API_get_report_tabs,
} from "../../../constants";
import axios from "axios";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import TabsTwo from "../requests/shared/helpers/TabsTwo";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SavedReportsModal from "./SavedReportsModal";
import SavedReportsView from "./SavedReportsView";
import { toast } from "react-toastify";
import {
  DATE_RANGE_GROUPS,
  getBusinessWeekStartDayIndex,
  getPresetDateRange,
} from "./dateRangePresets";
import {
  createStoreInfoLookup,
  groupStoreOptionsByCompany,
} from "../../../helpers/storeOptions";

const formatCategoryMap = {
  dollars: "Financial Metrics",
  hours: "Coverage Labor Hours",
  percentage: "Percentage Metrics",
  number: "Count Metrics",
  units: "Units",
  salesCounts: "Sales Counts",
};

const coverageCategoryLabel = formatCategoryMap.hours;
const nonCoverageCategoryLabel = "Non Coverage Labor Hours";

const nonCoverageMetricValues = new Set([
  "scheduled_training_hours",
  "actual_training_hours",
  "scheduled_admin_hours",
  "actual_admin_hours",
]);

const companySpecificFinancialMetricValues = new Set([
  "actual_sales_vs_actual_sellable_hours",
]);

const availableStaticMetrics = [
  {
    value: "forecasted_sales",
    label: "Forecasted Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_adjusted_sales",
    label: "Forecasted Adjusted Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_sales",
    label: "Actual Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_sales_previous_year",
    label: "Actual Sales (Previous Year)",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_labor_cost",
    label: "Actual Labor Cost",
    format: "dollars",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_sales_minus_labor_cost",
    label: "Actual Sales - Labor Cost",
    format: "dollars",
    Daily: true,
    Intraday: false,
  },
  {
    value: "splh",
    label: "Actual SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_splh",
    label: "Forecasted SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "scheduled_splh",
    label: "Scheduled SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_sales_vs_actual_sellable_hours",
    label: "Actual Sales vs Actual Sellable Hours",
    format: "dollars",
    Daily: true,
    Intraday: false,
    company_specific: 87,
  },
  {
    value: "earned_splh",
    label: "Earned SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "scheduled_labor_hours",
    label: "Scheduled Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_labor_hours",
    label: "Forecasted Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_labor_hours",
    label: "Actual Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "earned_labor_hours",
    label: "Earned Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "logbook_entries",
    label: "Logbook Entries",
    format: "number",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_forecasted_sales",
    label: "Actual to Forecasted Sales",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_forecasted_adjusted_sales",
    label: "Actual to Forecasted Adjusted Sales",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "scheduled_to_forecasted_hours",
    label: "Scheduled to Forecasted Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_earned_hours",
    label: "Actual to Earned Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_scheduled_hours",
    label: "Actual to Scheduled Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "earned_to_forecasted_hours",
    label: "Earned to Forecasted Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
];

const laborPercentMetric = {
  value: "labor_percent",
  label: "Labor Percent",
  format: "percentage",
  Daily: true,
  Intraday: false,
};

const getLaborPercentEnabled = (storeNames, storeId) => {
  try {
    return Boolean(
      storeNames.find((store) => String(store.id) === String(storeId))
        ?.labor_percent_enabled
    );
  } catch (error) {
    return false;
  }
};

const getAvailableStaticMetrics = (laborPercentEnabled) => {
  if (!laborPercentEnabled) {
    return availableStaticMetrics;
  }

  return [...availableStaticMetrics, laborPercentMetric];
};

function ReportingDisplay(props) {
  const axiosConfig = getDefaultAxiosConfig();
  const laborPercentEnabled = getLaborPercentEnabled(
    props.storeNames,
    props.homeStore
  );
  const staticMetrics = React.useMemo(
    () => getAvailableStaticMetrics(laborPercentEnabled),
    [laborPercentEnabled]
  );
  const [comparingMetricsFlag, setComparingMetricsFlag] = useState(false);
  const [selectedStores, setSelectedStores] = useState(() =>
    props.homeStore !== undefined && props.homeStore !== null
      ? [String(props.homeStore)]
      : []
  );
  const [selectedMetrics, setSelectedMetrics] = useState(["forecasted_sales"]);
  const [availableMetrics, setAvailableMetrics] = useState(staticMetrics);
  const [calculatedMetrics, setCalculatedMetrics] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [data, setData] = useState([]);
  const [intradayData, setIntradayData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [calculationStatus, setCalculationStatus] = useState("idle");
  const [downloadStatus, setDownloadStatus] = useState("notavailable");
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);

  // Save Report State
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState("");
  const [selectedTabForReport, setSelectedTabForReport] = useState(null); // Tab to assign report to
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedReports, setSavedReports] = useState([]);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [showSavedReportsDialog, setShowSavedReportsDialog] = useState(false);
  const [showManageReports, setShowManageReports] = useState(false);
  const [reportTabs, setReportTabs] = useState([]);

  // Widget type selection state
  const [selectedWidgetType, setSelectedWidgetType] = useState("line");

  const [selectedDateRange, setSelectedDateRange] = React.useState("");
  const [showCustomDateRange, setShowCustomDateRange] = React.useState(false);
  const [compareLocationsEligible, setCompareLocationsEligible] =
    useState(false);
  const businessWeekStartDayIndex = getBusinessWeekStartDayIndex(
    props.storeNames,
    props.homeStore
  );

  const storeInfoLookup = React.useMemo(
    () => createStoreInfoLookup(props.storeNames),
    [props.storeNames]
  );

  const groupedStoreOptions = React.useMemo(() => {
    if (!Array.isArray(props.storeNames)) {
      return [];
    }

    const baseOptions = props.storeNames
      .filter((store) => store && store.id !== undefined && store.id !== null)
      .map((store) => ({ store: store.id }));

    return groupStoreOptionsByCompany(baseOptions, storeInfoLookup);
  }, [props.storeNames, storeInfoLookup]);

  const showStoreCompanyHeaders = groupedStoreOptions.length > 1;

  const selectedStoreSet = React.useMemo(
    () => new Set(selectedStores),
    [selectedStores]
  );

  const storeMenuItemsWithCheckbox = React.useMemo(() => {
    return groupedStoreOptions.flatMap((group) => {
      const items = [];
      const headerKey = group.companyId || `company-${group.companyName}`;

      if (showStoreCompanyHeaders) {
        items.push(
          <MenuItem
            key={`store-header-${headerKey}`}
            disabled
            sx={{
              fontWeight: "bold",
              opacity: 1,
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            {group.companyName}
          </MenuItem>
        );
      }

      group.options.forEach((option) => {
        const storeId = option.store;
        const storeInfo = storeInfoLookup.get(storeId);

        if (!storeInfo) {
          return;
        }

        const storeValue = String(storeId);
        const isChecked = selectedStoreSet.has(storeValue);

        items.push(
          <MenuItem key={`store-${storeValue}`} value={storeValue}>
            <Checkbox
              checked={isChecked}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
              }}
            />
            <ListItemText primary={storeInfo.store_name} />
          </MenuItem>
        );
      });

      return items;
    });
  }, [
    groupedStoreOptions,
    showStoreCompanyHeaders,
    storeInfoLookup,
    selectedStoreSet,
  ]);

  const storeMenuItemsSingle = React.useMemo(() => {
    return groupedStoreOptions.flatMap((group) => {
      const items = [];
      const headerKey = group.companyId || `company-${group.companyName}`;

      if (showStoreCompanyHeaders) {
        items.push(
          <MenuItem
            key={`single-store-header-${headerKey}`}
            disabled
            sx={{
              fontWeight: "bold",
              opacity: 1,
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            {group.companyName}
          </MenuItem>
        );
      }

      group.options.forEach((option) => {
        const storeId = option.store;
        const storeInfo = storeInfoLookup.get(storeId);

        if (!storeInfo) {
          return;
        }

        const storeValue = String(storeId);

        items.push(
          <MenuItem key={`single-store-${storeValue}`} value={storeValue}>
            {storeInfo.store_name}
          </MenuItem>
        );
      });

      return items;
    });
  }, [groupedStoreOptions, showStoreCompanyHeaders, storeInfoLookup]);

  // Daily vs Intraday Tabs
  const [activeTab, setActiveTab] = React.useState("Daily");
  function handleTabButtonPress(target) {
    setActiveTab(target);
    if (selectedRadioOption === "Compare by Day Of Week") {
      setSelectedRadioOption("Default");
    }
    // Filter out any selectedMetrics that are not valid for this new tab.
    // For example, if the tab is "Intraday," we only keep metrics where metric.Intraday === true.
    const validMetricValuesForTab = availableMetrics
      .filter((metric) => metric[target]) // e.g. metric["Daily"], metric["Intraday"]
      .map((metric) => metric.value);

    const filteredSelectedMetrics = selectedMetrics.filter((metricValue) =>
      validMetricValuesForTab.includes(metricValue)
    );

    // If any are invalid, update `selectedMetrics` state so we remove them
    if (filteredSelectedMetrics.length !== selectedMetrics.length) {
      setSelectedMetrics(filteredSelectedMetrics);
    }
  }

  console.log(selectedMetrics);
  useEffect(() => {
    // We only need to fetch metrics once, or whenever homeStore changes.
    fetchMetrics();
    fetchSavedReports();
    fetchReportTabs();
  }, [props.homeStore, staticMetrics]);

  function fetchMetrics() {
    const url = API_get_company_metrics;
    const params = { store_id: props.homeStore };

    axios
      .get(url, { params, ...axiosConfig })
      .then((res) => {
        // res.data should be an array of metric objects like:
        // [{ value: "forecasted_sales", label: "Forecasted Sales", format: "dollars", ... }, ...]
        const staticMetricValues = new Set(
          staticMetrics.map((metric) => metric.value)
        );
        const dynamicMetrics = (res.data || []).filter(
          (metric) => !staticMetricValues.has(metric.value)
        );
        setAvailableMetrics([...staticMetrics, ...dynamicMetrics]);
      })
      .catch((error) => {
        console.error("Error fetching metrics:", error);
      });
  }

  const fetchReportTabs = async () => {
    if (!props.homeStore) return;

    try {
      const response = await axios.get(API_get_report_tabs, {
        params: { store_id: props.homeStore },
        ...axiosConfig,
      });
      console.log('Fetched report tabs:', response.data);
      setReportTabs(response.data || []);
    } catch (error) {
      console.error("Error fetching tabs:", error);
      setReportTabs([]);
    }
  };

  // Radio Tabs
  const [selectedRadioOption, setSelectedRadioOption] = useState("Default");
  let radioOptions = [
    "Default",
    "Compare by Store",
    "Compare by Metric",
    "Compare to Previous Year",
  ];
  if (activeTab === "Daily") {
    radioOptions = [
      "Default",
      "Compare by Store",
      "Compare by Metric",
      "Compare to Previous Year",
    ];
  } else {
    radioOptions = [
      "Default",
      "Compare by Store",
      "Compare by Metric",
      "Compare by Day Of Week",
    ];
  }

  const handleCheckChange = (event) => {
    if (selectedMetrics.length > 1) {
      setCalculationStatus("idle");
    }
  };

  function toggleCustomDateRange(forceState) {
    if (typeof forceState === "boolean") {
      setShowCustomDateRange(forceState);
      if (forceState) {
        setSelectedDateRange("custom");
      }
      return;
    }

    setShowCustomDateRange((prevState) => {
      const nextState = !prevState;
      if (nextState) {
        setSelectedDateRange("custom");
      }
      return nextState;
    });
  }

  function handleDateRangeChange(event) {
    const { value } = event.target;
    setSelectedDateRange(value);

    const { startDate, endDate, requiresCustomInput } = getPresetDateRange({
      preset: value,
      today: new Date(),
      businessWeekStartDayIndex,
    });

    if (requiresCustomInput) {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      toggleCustomDateRange(true);
      return;
    }

    if (value !== "custom" && showCustomDateRange) {
      toggleCustomDateRange(false);
    }

    if (startDate && endDate) {
      setSelectedStartDate(new Date(startDate));
      setSelectedEndDate(new Date(endDate));
    } else {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  }

  const dateRangeGroups = DATE_RANGE_GROUPS;

  const dateRangeMenuItems = React.useMemo(() => {
    return dateRangeGroups.reduce((items, group) => {
      items.push(
        <MenuItem
          key={`${group.label}-header`}
          disabled
          sx={{ fontWeight: "bold", opacity: 1 }}
        >
          {group.label}
        </MenuItem>
      );

      group.options.forEach((option) => {
        items.push(
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              backgroundColor: selectedDateRange === option.value
                ? "#f3e8ff"
                : "inherit",
            }}
          >
            {option.label}
          </MenuItem>
        );
      });

      return items;
    }, []);
  }, [dateRangeGroups, selectedDateRange]);

  let compareLocationsChecked = selectedRadioOption === "Compare by Store";
  let compareDow = selectedRadioOption === "Compare by Day Of Week";

  useEffect(() => {
    // Clear out previous calculation results
    setData([]);
    setIntradayData([]);
    setRawData([]);
    setSummaryData([]);
    setLocationData([]);
    setCalculatedMetrics([]);

    setCalculationStatus("idle");
    setDownloadStatus("notavailable");
  }, [selectedRadioOption, activeTab]);

  const handleStoreChange = (event) => {
    const {
      target: { value },
    } = event;

    const rawValue = Array.isArray(value)
      ? value
      : typeof value === "string"
      ? value.split(",")
      : [value];

    const normalizedValue = rawValue.map((item) => String(item));

    if (normalizedValue.includes("all")) {
      if (selectedStores.length === props.storeNames.length) {
        setSelectedStores([]);
        setCompareLocationsEligible(false);
      } else {
        const allStoreIds = props.storeNames
          .filter((store) => store && store.id !== undefined && store.id !== null)
          .map((store) => String(store.id));
        setSelectedStores(allStoreIds);
        setCompareLocationsEligible(allStoreIds.length > 1);
      }
      return;
    }

    const filteredValue = normalizedValue.filter((item) => item !== "");

    setSelectedStores(filteredValue);
    setCompareLocationsEligible(filteredValue.length > 1);
  };

  const handleMetricChange = (event) => {
    const {
      target: { value },
    } = event;
    const metrics = typeof value === "string" ? value.split(",") : value;
    setSelectedMetrics(metrics);
  };

  useEffect(() => {
    setCalculationStatus("idle");
  }, [selectedStores, selectedMetrics, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (calculationStatus === "completed") {
      setDownloadStatus("available");
    } else {
      setDownloadStatus("notavailable");
    }
  }, [calculationStatus]);

  const calculateMetric = () => {
    setCalculationStatus("loading");

    const params = {
      selectedStores: selectedStores.join(","),
      selectedMetrics: selectedMetrics.join(","),
      selectedStartDate: formatDate(selectedStartDate),
      selectedEndDate: formatDate(selectedEndDate),
      selectedDateRange,
      store_id: props.homeStore,
      comparingMetricsFlag: comparingMetricsFlag,
      activeTab: activeTab,
      selectedRadioOption: selectedRadioOption,
    };

    const metricObjs = selectedMetrics.map((m) =>
      availableMetrics.find((am) => am.value === m)
    );
    const allAnalytics = metricObjs.every((m) => m && m.analytics);
    const url = allAnalytics ? API_get_event_metrics : API_calculate_metric;

    axios
      .get(url, { params: params, ...axiosConfig })
      .then((res) => {
        const newData = res.data.redux.payload.agg_data.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        const newRawData = res.data.redux.payload.raw_data.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        const newSummaryData = res.data.redux.payload.summary_data;
        const newLocationData = res.data.redux.payload.location_data;
        const newIntradayData = res.data.redux.payload.intraday_data.sort(
          (a, b) => a.qh_num - b.qh_num
        );
        console.log("newData", newData);
        setIntradayData(newIntradayData);
        setData(newData);
        setRawData(newRawData);
        setSummaryData(newSummaryData);
        setLocationData(newLocationData);
        const calculated = [...selectedMetrics];
        if (selectedRadioOption === "Compare to Previous Year") {
          selectedMetrics.forEach((m) => {
            // Don't add _previous_year suffix if metric already has it (prevents 2 years ago)
            if (!m.endsWith("_previous_year")) {
              calculated.push(`${m}_previous_year`);
            }
          });
        }
        setCalculatedMetrics(calculated);
        setCalculationStatus("completed");
      })
      .catch((error) => {
        console.error(error);
        setCalculationStatus("idle");
      });
  };

  function getMetricInfo(metricValue) {
    if (!metricValue) {
      return { label: "N/A", format: "N/A" };
    }

    let baseValue = metricValue;
    let suffix = "";

    if (metricValue.endsWith("_previous_year")) {
      baseValue = metricValue.replace("_previous_year", "");
      suffix = " (Prev Year)";
    }

    const metric = availableMetrics.find((m) => m.value === baseValue);

    if (metric) {
      return {
        label: `${metric.label}${suffix}`,
        format: metric.format,
      };
    }

    return { label: "N/A", format: "N/A" };
  }

  function getMetricLabel(metricValue) {
    return getMetricInfo(metricValue).label.replace(/\s+/g, "_");
  }

  function getDetailedExportLabel() {
    if (activeTab === "Weekly") return "Weekly Detail";
    return "Daily Detail";
  }

  function aggregateRowsAcrossTimePeriod(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const metricAggregationStrategies = {
      actual_sales_minus_labor_cost: {
        type: "difference",
        supportKeys: ["actual_sales", "labor_cost"],
      },
      labor_percent: {
        type: "ratio",
        supportKeys: ["labor_cost", "actual_sales"],
      },
      splh: {
        type: "ratio",
        supportKeys: ["sales", "hours"],
      },
      forecasted_splh: {
        type: "ratio",
        supportKeys: ["forecasted_splh_sales", "forecasted_splh_hours"],
      },
      scheduled_splh: {
        type: "ratio",
        supportKeys: ["scheduled_splh_sales", "scheduled_splh_hours"],
      },
      actual_sales_vs_actual_sellable_hours: {
        type: "ratio",
        supportKeys: ["actual_sales", "actual_sellable_hours"],
      },
      earned_splh: {
        type: "ratio",
        supportKeys: ["sales", "hours"],
      },
      actual_to_forecasted_sales: {
        type: "ratio",
        supportKeys: ["actual_sales", "forecasted_sales"],
      },
      actual_to_forecasted_adjusted_sales: {
        type: "ratio",
        supportKeys: ["actual_sales", "forecasted_sales_adjusted"],
      },
      scheduled_to_forecasted_hours: {
        type: "ratio",
        supportKeys: ["scheduled_hours", "forecasted_hours"],
      },
      actual_to_earned_hours: {
        type: "ratio",
        supportKeys: ["actual_hours", "earned_hours"],
      },
      actual_to_scheduled_hours: {
        type: "ratio",
        supportKeys: ["actual_hours", "scheduled_hours"],
      },
      earned_to_forecasted_hours: {
        type: "ratio",
        supportKeys: ["earned_hours", "forecasted_hours"],
      },
      entrees_plh: {
        type: "ratio",
        supportKeys: ["units", "hours"],
      },
    };
    const supportKeyMetricAliases = {
      labor_cost: "actual_labor_cost",
      scheduled_hours: "scheduled_labor_hours",
      forecasted_hours: "forecasted_labor_hours",
      actual_hours: "actual_labor_hours",
      earned_hours: "earned_labor_hours",
    };
    const timeFieldKeys = new Set([
      "date",
      "qh_num",
      "day_of_week",
      "week_start",
      "week_end",
      "week",
      "month",
      "year",
    ]);
    const selectedMetricSet = new Set([
      ...selectedMetrics,
      ...calculatedMetrics,
    ]);
    const availableRowColumns = new Set(
      rows.flatMap((row) => (row && typeof row === "object" ? Object.keys(row) : []))
    );
    const availableRowColumnList = Array.from(availableRowColumns);
    const getSupportColumnCandidates = (supportKey) => {
      const candidates = [];
      if (availableRowColumns.has(supportKey)) {
        candidates.push(supportKey);
      }

      availableRowColumnList.forEach((column) => {
        if (!column.startsWith(`${supportKey}_`)) return;
        const suffix = column.slice(supportKey.length + 1);
        if (suffix === "x" || suffix === "y") {
          candidates.push(column);
        }
      });

      return candidates;
    };
    const getFirstNumericValue = (row, columns) => {
      for (const column of columns) {
        const rawValue = row[column];
        if (rawValue === null || rawValue === undefined) continue;
        const numericValue = Number(rawValue);
        if (!Number.isNaN(numericValue)) {
          return numericValue;
        }
      }
      return null;
    };
    // Two metrics can carry different values under one component name
    // (splh's actual hours vs earned_splh's earned hours, or last year's
    // components), so the backend keeps the second copy as
    // `<metric>_<component>`. Prefer that column; a previous-year metric only
    // uses its own, since the plain one holds this year's values.
    const getMetricSupportColumns = (metric, supportKey) => {
      const qualifiedColumn = `${metric}_${supportKey}`;
      if (availableRowColumns.has(qualifiedColumn)) return [qualifiedColumn];
      if (metric.endsWith("_previous_year")) return [];
      return getSupportColumnCandidates(supportKey);
    };
    const metricAggregationSupport = Array.from(selectedMetricSet).reduce(
      (acc, metric) => {
        const strategy =
          metricAggregationStrategies[metric] ||
          metricAggregationStrategies[metric.replace(/_previous_year$/, "")];
        if (!strategy) return acc;

        const supportColumnsByKey = strategy.supportKeys.reduce(
          (supportAcc, supportKey) => {
            const columns = getMetricSupportColumns(metric, supportKey);
            if (columns.length > 0) {
              supportAcc[supportKey] = columns;
            }
            return supportAcc;
          },
          {}
        );
        const hasAllSupportKeys = strategy.supportKeys.every(
          (supportKey) => supportColumnsByKey[supportKey]?.length > 0
        );
        if (!hasAllSupportKeys) return acc;

        acc[metric] = {
          ...strategy,
          supportColumnsByKey,
        };
        return acc;
      },
      {}
    );
    const supportFieldKeys = new Set(
      Object.values(metricAggregationSupport).flatMap(({ supportKeys }) => supportKeys)
    );
    const supportColumnsByField = Array.from(supportFieldKeys).reduce(
      (acc, supportKey) => {
        acc[supportKey] = getSupportColumnCandidates(supportKey);
        return acc;
      },
      {}
    );
    const metricQualifiedColumns = availableRowColumnList.filter(
      (column) =>
        !selectedMetricSet.has(column) &&
        Array.from(selectedMetricSet).some((metric) => column.startsWith(`${metric}_`))
    );
    const supportFieldColumns = new Set([
      ...Object.values(supportColumnsByField).flat(),
      ...Object.values(metricAggregationSupport).flatMap(({ supportColumnsByKey }) =>
        Object.values(supportColumnsByKey).flat()
      ),
      ...metricQualifiedColumns,
    ]);
    const selectedAliasedSupportMetrics = Array.from(supportFieldKeys).reduce(
      (acc, supportKey) => {
        const aliasedMetric = supportKeyMetricAliases[supportKey];
        if (!aliasedMetric) return acc;
        if (!selectedMetricSet.has(aliasedMetric)) return acc;
        if (availableRowColumns.has(aliasedMetric)) return acc;
        acc[supportKey] = aliasedMetric;
        return acc;
      },
      {}
    );
    const aggregationMetricKeys = Array.from(selectedMetricSet).filter((metric) =>
      availableRowColumns.has(metric) ||
      Object.values(selectedAliasedSupportMetrics).includes(metric)
    );
    const metricFormats = aggregationMetricKeys.reduce((acc, metric) => {
      acc[metric] = getMetricInfo(metric).format;
      return acc;
    }, {});

    const groupedRows = new Map();
    rows.forEach((row) => {
      if (!row || typeof row !== "object") return;

      const dimensionEntries = Object.entries(row).filter(
        ([key]) =>
          !timeFieldKeys.has(key) &&
          !selectedMetricSet.has(key) &&
          !supportFieldKeys.has(key) &&
          !supportFieldColumns.has(key)
      );
      const groupKey =
        dimensionEntries.map(([key, value]) => `${key}:${String(value)}`).join("|") ||
        "__all__";

      if (!groupedRows.has(groupKey)) {
        const seed = Object.fromEntries(dimensionEntries);
        seed.period_start = formatDate(selectedStartDate);
        seed.period_end = formatDate(selectedEndDate);
        seed.aggregation_level = "time_period";
        seed.__metric_counts = {};
        seed.__metric_support = {};
        aggregationMetricKeys.forEach((metric) => {
          seed[metric] = 0;
          seed.__metric_counts[metric] = 0;
        });
        supportFieldKeys.forEach((supportKey) => {
          seed[supportKey] = 0;
        });
        groupedRows.set(groupKey, seed);
      }

      const aggregateRow = groupedRows.get(groupKey);
      supportFieldKeys.forEach((supportKey) => {
        const numericValue = getFirstNumericValue(
          row,
          supportColumnsByField[supportKey] || [supportKey]
        );
        if (numericValue === null) return;
        aggregateRow[supportKey] += numericValue;
        const aliasedMetric = selectedAliasedSupportMetrics[supportKey];
        if (aliasedMetric) {
          aggregateRow[aliasedMetric] += numericValue;
        }
      });
      Object.entries(metricAggregationSupport).forEach(
        ([metric, { supportColumnsByKey }]) => {
          if (!aggregateRow.__metric_support[metric]) {
            aggregateRow.__metric_support[metric] = {};
          }
          const metricSupport = aggregateRow.__metric_support[metric];
          Object.entries(supportColumnsByKey).forEach(([supportKey, columns]) => {
            const numericValue = getFirstNumericValue(row, columns);
            if (numericValue === null) return;
            metricSupport[supportKey] = (metricSupport[supportKey] || 0) + numericValue;
          });
        }
      );
      aggregationMetricKeys.forEach((metric) => {
        if (metricAggregationSupport[metric] || supportFieldKeys.has(metric)) return;
        const rawMetricValue = row[metric];
        if (rawMetricValue === null || rawMetricValue === undefined) return;
        const numericValue = Number(rawMetricValue);
        if (Number.isNaN(numericValue)) return;
        aggregateRow[metric] += numericValue;
        aggregateRow.__metric_counts[metric] += 1;
      });
    });

    return Array.from(groupedRows.values()).map((row) => {
      const normalizedRow = { ...row };
      aggregationMetricKeys.forEach((metric) => {
        const strategy = metricAggregationSupport[metric];
        if (strategy) {
          const [leftKey, rightKey] = strategy.supportKeys;
          const metricSupport = row.__metric_support[metric] || {};
          if (strategy.type === "difference") {
            normalizedRow[metric] =
              Number(metricSupport[leftKey] || 0) - Number(metricSupport[rightKey] || 0);
          } else {
            const numerator = Number(metricSupport[leftKey] || 0);
            const denominator = Number(metricSupport[rightKey] || 0);
            normalizedRow[metric] = denominator !== 0 ? numerator / denominator : 0;
          }
          return;
        }
        if (
          metricFormats[metric] === "percentage" &&
          normalizedRow.__metric_counts[metric] > 0
        ) {
          normalizedRow[metric] =
            normalizedRow[metric] / normalizedRow.__metric_counts[metric];
        }
      });
      supportFieldKeys.forEach((supportKey) => {
        if (selectedMetricSet.has(supportKey)) return;
        delete normalizedRow[supportKey];
      });
      delete normalizedRow.__metric_counts;
      delete normalizedRow.__metric_support;
      return normalizedRow;
    });
  }

  function handleDownload(mode) {
    setDownloadStatus("downloading");
    const rowsForDownload =
      mode === "aggregated"
        ? aggregateRowsAcrossTimePeriod(rawData)
        : rawData;
    const csvData = objectToCSV(rowsForDownload);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const fileName = `${selectedMetrics
      .map(getMetricLabel)
      .join("_")}_${formatDate(selectedStartDate)}_to_${formatDate(
      selectedEndDate
    )}_${mode}.csv`;

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
    setDownloadStatus("completed");
  }

  function handleDownloadMenuOpen(event) {
    setDownloadMenuAnchorEl(event.currentTarget);
  }

  function handleDownloadMenuClose() {
    setDownloadMenuAnchorEl(null);
  }

  function handleDownloadMenuSelect(mode) {
    handleDownloadMenuClose();
    handleDownload(mode);
  }

  function objectToCSV(obj) {
    if (!obj || obj.length === 0) return "";
    const header = Object.keys(obj[0]).join(",");
    const rows = obj.map((row) => Object.values(row).join(","));
    return [header, ...rows].join("\n");
  }

  // Save Report Functions
  const handleSaveReport = () => {
    if (!checkIfSubmitValid() || calculationStatus !== "completed") {
      return;
    }
    console.log('Opening save dialog. Report tabs:', reportTabs);
    setShowSaveDialog(true);
  };

  const handleSaveReportConfirm = () => {
    if (!reportName.trim()) {
      return;
    }

    setSaveStatus("saving");

    const reportData = {
      name: reportName.trim(),
      selected_stores: selectedStores,
      selected_metrics: selectedMetrics,
      selected_date_range: selectedDateRange,
      custom_start_date: selectedDateRange === "custom" ? formatDate(selectedStartDate) : null,
      custom_end_date: selectedDateRange === "custom" ? formatDate(selectedEndDate) : null,
      active_tab: activeTab,
      selected_radio_option: selectedRadioOption,
      widget_type: selectedWidgetType,
      tab_id: selectedTabForReport,  // Include the selected tab
      store_id: props.homeStore  // Required for API authentication
    };

    console.log('Saving report with data:', reportData);

    axios
      .post(API_save_report, reportData, axiosConfig)
      .then((res) => {
        setSaveStatus("completed");
        setReportName("");
        setSelectedTabForReport(null);  // Reset tab selection
        setShowSaveDialog(false);
        fetchSavedReports(); // Refresh the saved reports list
      })
      .catch((error) => {
        console.error("Error saving report:", error);
        setSaveStatus("error");
      });
  };

  const fetchSavedReports = () => {
    setSavedReportsLoading(true);
    const params = {
      store_id: props.homeStore  // Required for API authentication
    };

    axios
      .get(API_get_saved_reports, { params, ...axiosConfig })
      .then((res) => {
        setSavedReports(res.data || []);
      })
      .catch((error) => {
        console.error("Error fetching saved reports:", error);
        setSavedReports([]);
      })
      .finally(() => {
        setSavedReportsLoading(false);
      });
  };

  const handleDeleteSavedReport = async (reportId) => {
    const params = {
      store_id: props.homeStore  // Required for API authentication
    };

    // Show loading toast
    toast.loading("Deleting report...", { autoClose: false });

    try {
      await axios.delete(API_delete_saved_report + "/" + reportId, { params, ...axiosConfig });
      // Update the list by filtering out the deleted report
      setSavedReports(prevReports => prevReports.filter(report => report.id !== reportId));
      // Refresh tabs to update the report counts
      await fetchReportTabs();

      // Dismiss loading toast and show success
      toast.dismiss();
      toast.success("Report deleted successfully!", { autoClose: 3000 });
    } catch (error) {
      console.error("Error deleting saved report:", error);
      // Dismiss loading toast and show error
      toast.dismiss();
      toast.error("Failed to delete report", { autoClose: 3000 });
    }
  };

  const handleOpenSavedReportsDialog = () => {
    setShowManageReports(true);
  };

  const handleGoBack = () => {
    setShowManageReports(false);
  };

  const handleReorderReports = (result) => {
    if (!result.destination) return;

    const reorderedReports = Array.from(savedReports);
    const [movedReport] = reorderedReports.splice(result.source.index, 1);
    reorderedReports.splice(result.destination.index, 0, movedReport);

    // Update local state immediately for better UX
    setSavedReports(reorderedReports);

    // Prepare the update payload
    const reportOrders = reorderedReports.map((report, index) => ({
      id: report.id,
      order: index
    }));

    // Send to backend
    const updateData = {
      store_id: props.homeStore,
      report_orders: reportOrders
    };

    axios
      .post(API_update_saved_reports_order, updateData, axiosConfig)
      .then((res) => {
        console.log('Report order updated successfully');
      })
      .catch((error) => {
        console.error('Error updating report order:', error);
        // Revert to original order on error
        fetchSavedReports();
      });
  };

  const handleLoadSavedReport = (report) => {
    const storedStores = Array.isArray(report.selected_stores)
      ? report.selected_stores.map((store) => String(store))
      : [];

    setSelectedStores(storedStores);
    setCompareLocationsEligible(storedStores.length > 1);
    setSelectedMetrics(report.selected_metrics);
    setSelectedDateRange(report.selected_date_range);
    setActiveTab(report.active_tab);
    setSelectedRadioOption(report.selected_radio_option);
    setSelectedWidgetType(report.widget_type || "line");

    // Apply date range logic
    if (report.selected_date_range === "custom" && report.custom_start_date && report.custom_end_date) {
      // Load custom dates using dateStrToObj to avoid timezone drift
      setSelectedStartDate(dateStrToObj(report.custom_start_date));
      setSelectedEndDate(dateStrToObj(report.custom_end_date));
      setShowCustomDateRange(true);
    } else if (report.selected_date_range && report.selected_date_range !== "custom") {
      // Load preset date range
      handleDateRangeChange({ target: { value: report.selected_date_range } });
      setShowCustomDateRange(false);
    }

    setShowManageReports(false);
  };

  const checkIfTypesValid = (compareMetrics) => {
    // Check if selected metrics have the same format type
    const firstMetricFormat =
      compareMetrics.length > 0
        ? availableMetrics.find((m) => m.value === compareMetrics[0])?.format
        : "";
    if (!firstMetricFormat) {
      return false;
    }
    const areMetricsSameFormat = compareMetrics.every(
      (metric) =>
        availableMetrics.find((m) => m.value === metric)?.format ===
        firstMetricFormat
    );
    return areMetricsSameFormat;
  };

  const areMetricsSameFormat = checkIfTypesValid(selectedMetrics);

  const checkIfSubmitValid = () => {
    let valid = true;
    if (selectedStores.length === 0 || selectedMetrics.length === 0) {
      valid = false;
    } else if (
      !selectedStartDate ||
      !selectedEndDate ||
      selectedStartDate > selectedEndDate
    ) {
      valid = false;
    } else if (!areMetricsSameFormat) {
      valid = false;
    } else if (compareLocationsChecked && selectedMetrics.length > 1) {
      valid = false;
    }
    return valid;
  };

  const enrichedSummaryData = summaryData.map((data) => {
    const metricInfo = getMetricInfo(data.metric);

    return {
      ...data,
      label: metricInfo.label,
      format: metricInfo.format,
    };
  });

  const enrichedLocationData = locationData.map((data) => {
    const metricInfo = getMetricInfo(data.metric);

    return {
      ...data,
      label: metricInfo.label,
      format: metricInfo.format,
    };
  });

  // console.log("LOCATION DATA", locationData);

  dayjs.extend(updateLocale);
  dayjs.updateLocale("en", {
    weekStart: businessWeekStartDayIndex,
  });

  const metricsForDropdown = availableMetrics
    .filter((metric) => metric[activeTab])
    .filter(
      (metric) =>
        !metric.company_specific ||
        metric.company_specific === props.homeCompany
    );

  const groupedMetrics = metricsForDropdown.reduce((acc, metric) => {
    let category;

    if (nonCoverageMetricValues.has(metric.value)) {
      category = nonCoverageCategoryLabel;
    } else if (companySpecificFinancialMetricValues.has(metric.value)) {
      category = formatCategoryMap.dollars;
    } else if (metric.company_specific) {
      category = formatCategoryMap.salesCounts;
    } else {
      category = formatCategoryMap[metric.format] || "Other";
    }
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(metric);
    return acc;
  }, {});

  const metricOptions = Object.entries(groupedMetrics)
    .map(([category, metrics], index) => ({
      category,
      metrics,
      originalIndex: index,
    }))
    .sort((a, b) => {
      const rank = (category) => {
        if (category === coverageCategoryLabel) return 0;
        if (category === nonCoverageCategoryLabel) return 1;
        return 2;
      };

      const rankDiff = rank(a.category) - rank(b.category);
      if (rankDiff !== 0) {
        return rankDiff;
      }
      return a.originalIndex - b.originalIndex;
    })
    .flatMap(({ category, metrics }) => [
      { type: "header", label: category },
      ...metrics.map((m) => ({ type: "metric", ...m })),
    ]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  // If showing manage reports, render the SavedReportsView
  if (showManageReports) {
    return (
      <SavedReportsView
        savedReports={savedReports}
        savedReportsLoading={savedReportsLoading}
        storeNames={props.storeNames}
        availableMetrics={availableMetrics}
        onDeleteReport={handleDeleteSavedReport}
        onReorderReports={handleReorderReports}
        onLoadReport={handleLoadSavedReport}
        showLoadButton={true}
        title="Saved Reports"
        description="Load a saved report to populate the reporting form. You can also drag and drop to reorder reports."
        onBack={handleGoBack}
        backButtonText="Back to Store Analytics"
        homeStore={props.homeStore}
        onRefreshReports={fetchSavedReports}
        tabs={reportTabs}
        onRefreshTabs={fetchReportTabs}
      />
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div>
        <TopBarDisplayTitleNoLocationSelect
          title={props.analyticsSwitcher ? "Analytics" : "Store Analytics"}
          titleExtra={props.analyticsSwitcher}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="mt-6 px-6 flex items-center space-x-16">
          <div className="w-[400px]">
            <TabsTwo
              activeTab={activeTab}
              handleTabButtonPress={handleTabButtonPress}
              tab1="Daily"
              tab2="Intraday"
            />
          </div>
          <div className="flex items-center space-x-6">
            {radioOptions.map((option) => (
              <div
                key={option}
                className="flex items-center cursor-pointer"
                onClick={() => {
                  // If user chooses something other than "By Metric"
                  // AND currently has multiple metrics selected,
                  // then we reduce it to only the first one.
                  if (
                    option !== "Compare by Metric" &&
                    selectedMetrics.length > 1
                  ) {
                    setSelectedMetrics([selectedMetrics[0]]);
                  }
                  setSelectedRadioOption(option);
                }}
              >
                <div
                  className={`
                    w-5 h-5 rounded-full border-2
                    ${
                      selectedRadioOption === option
                        ? "border-violet-700 bg-violet-700"
                        : "border-gray-300"
                    }
                    relative flex items-center justify-center
                  `}
                >
                  {selectedRadioOption === option && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <span className="ml-2 text-sm text-gray-700">{option}</span>
              </div>
            ))}
          </div>
        </div>
        {props.onOpenAIInsights && (
          <div className="mt-6 px-6">
            <button
              onClick={props.onOpenAIInsights}
              title="Chat with reports"
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-violet-700 hover:text-violet-700 transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 18 }} />
              <span>Chat with reports</span>
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-4 px-6 mt-6">
        <div className="w-[220px]">
          <div>
            <h4 className="text-slate-500 text-sm">
              Locations{selectedStores.length > 1 && ` (${selectedStores.length})`}
            </h4>
          </div>
          <div className="mt-1.5">
            {selectedRadioOption === "Default" ||
            selectedRadioOption === "Compare by Store" ||
            selectedRadioOption === "Compare by Metric" ||
            selectedRadioOption === "Compare by Day Of Week" ||
            selectedRadioOption === "Compare to Previous Year" ? (
              <Select
                label="Locations"
                multiple
                value={selectedStores}
                onChange={handleStoreChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  width: "100%",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                renderValue={(selected) => {
                  const selectionArray = Array.isArray(selected)
                    ? selected
                    : [selected];
                  const selectedSet = new Set(selectionArray);
                  const selectedStoresNames = props.storeNames
                    .filter((store) => selectedSet.has(String(store.id)))
                    .map((store) => store.store_name);

                  if (selectedStoresNames.length <= 2) {
                    return selectedStoresNames.join(", ");
                  } else {
                    return `${selectedStoresNames[0]}, ${selectedStoresNames[1]}, ...`;
                  }
                }}
                className="w-full"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      props.storeNames.length > 0 &&
                      selectedStores.length === props.storeNames.length
                    }
                    indeterminate={
                      selectedStores.length > 0 &&
                      selectedStores.length < props.storeNames.length
                    }
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <ListItemText
                    primary={
                      selectedStores.length === props.storeNames.length
                        ? "Deselect All"
                        : "Select All"
                    }
                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  />
                </MenuItem>
                {storeMenuItemsWithCheckbox}
              </Select>
            ) : (
              <Select
                fullWidth
                value={selectedStores[0] ?? ""}
                onChange={handleStoreChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  width: "100%",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                {storeMenuItemsSingle}
              </Select>
            )}
          </div>
        </div>

        <div className="w-[260px]">
          <div>
            <h4 className="text-slate-500 text-sm">
              Metrics{selectedMetrics.length > 1 && ` (${selectedMetrics.length})`}
            </h4>
          </div>
          <div className="mt-1.5">
            {selectedRadioOption === "Compare by Metric" ? (
              <Select
                label="Metrics"
                multiple
                value={selectedMetrics}
                onChange={handleMetricChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  width: "100%",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                renderValue={(selected) => {
                  const selectedMetricsLabels = availableMetrics
                    .filter((metric) => selected.includes(metric.value))
                    .map((metric) => metric.label);

                  if (selectedMetricsLabels.length <= 2) {
                    return selectedMetricsLabels.join(", ");
                  } else {
                    return `${selectedMetricsLabels[0]}, ${selectedMetricsLabels[1]}, ...`;
                  }
                }}
                className="w-full"
              >
                {metricOptions.map((item, index) =>
                  item.type === "header" ? (
                    <MenuItem
                      key={`header-${index}`}
                      disabled
                      sx={{ fontWeight: "bold", opacity: 1 }}
                    >
                      {item.label}
                    </MenuItem>
                  ) : (
                    <MetricOptionMenuItem
                      metric={item}
                      key={item.value}
                      value={item.value}
                      sx={{
                        backgroundColor: selectedMetrics.includes(item.value)
                          ? "#f3e8ff"
                          : "inherit",
                      }}
                    >
                      <Checkbox
                        checked={selectedMetrics.includes(item.value)}
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": { color: "#6d28d9" },
                        }}
                      />
                    </MetricOptionMenuItem>
                  )
                )}
              </Select>
            ) : (
              <Select
                fullWidth
                renderValue={(selected) => availableMetrics
                  .filter((metric) => (Array.isArray(selected) ? selected : [selected]).includes(metric.value))
                  .map((metric) => metric.label).join(", ")}
                value={selectedMetrics}
                onChange={handleMetricChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  width: "100%",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                {metricOptions.map((item, index) =>
                  item.type === "header" ? (
                    <MenuItem
                      key={`header-${index}`}
                      disabled
                      sx={{ fontWeight: "bold", opacity: 1 }}
                    >
                      {item.label}
                    </MenuItem>
                  ) : (
                    <MetricOptionMenuItem key={item.value} value={item.value} metric={item} />
                  )
                )}
              </Select>
            )}
          </div>
        </div>

        {showCustomDateRange ? (
          <div className="flex gap-2 flex-1 min-w-[380px]">
            <div className="flex-1 min-w-[170px]">
              <div>
                <h4 className="text-slate-500 text-sm">Start Date</h4>
              </div>
              <div className="mt-1.5">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    Text="Start Date"
                    value={selectedStartDate}
                    onChange={(newValue) => setSelectedStartDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                          paddingX: "25px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                    className="w-full text-center"
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="flex-1 min-w-[170px]">
              <div>
                <h4 className="text-slate-500 text-sm">End Date</h4>
              </div>
              <div className="mt-1.5">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    Text="End Date"
                    value={selectedEndDate}
                    onChange={(newValue) => setSelectedEndDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                          paddingX: "25px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                    className="w-full text-center"
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="flex items-end">
              <Tooltip title="Back to preset date ranges">
                <div
                  className="w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setSelectedDateRange("");
                    setSelectedStartDate(null);
                    setSelectedEndDate(null);
                    toggleCustomDateRange(false);
                  }}
                >
                  <UndoIcon />
                </div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-[220px]">
            <div>
              <h4 className="text-slate-500 text-sm">Date Range</h4>
            </div>
            <div className="mt-1.5">
              <Select
                fullWidth
                value={selectedDateRange}
                onChange={(event) => {
                  handleDateRangeChange(event);
                }}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                {dateRangeMenuItems}
              </Select>
            </div>
          </div>
        )}

        <div className="w-[180px]">
          <div>
            <h4 className="text-slate-500 text-sm">Visualization</h4>
          </div>
          <div className="mt-1.5">
            <Select
              fullWidth
              value={selectedWidgetType}
              onChange={(event) => setSelectedWidgetType(event.target.value)}
              input={<OutlinedInput />}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                paddingX: "15px",
                height: "40px",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
              className="w-full"
            >
              <MenuItem value="line">Line Chart</MenuItem>
              <MenuItem value="bar">Bar Chart</MenuItem>
              <MenuItem value="pie">Pie Chart</MenuItem>
              {/* <MenuItem value="gauge">Gauge</MenuItem> */}
              <MenuItem value="table">Table</MenuItem>
              <MenuItem value="cards">Metric Cards</MenuItem>
            </Select>
          </div>
        </div>

        <div className="flex items-end gap-3 self-end">
          <LoadingButton
            variant="contained"
            loading={calculationStatus === "loading"}
            loadingPosition="end"
            endIcon={
              calculationStatus === "completed" ? (
                <CheckIcon />
              ) : (
                <AutoGraphIcon />
              )
            }
            onClick={() => calculateMetric()}
            disabled={!checkIfSubmitValid() || calculationStatus === "loading"}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "40px",
              backgroundColor:
                calculationStatus === "completed" ? "green" : "#6d28d9",
              "&:hover": {
                backgroundColor:
                  calculationStatus === "completed" ? "darkgreen" : "#5b21b6",
              },
            }}
          >
            {calculationStatus === "loading"
              ? "Calculating..."
              : calculationStatus === "completed"
              ? "Metric Calculated"
              : "Calculate Metric"}
          </LoadingButton>

          <LoadingButton
            variant="contained"
            loading={downloadStatus === "downloading"}
            loadingPosition="end"
            endIcon={
              downloadStatus === "completed" ? <CheckIcon /> : <DownloadIcon />
            }
            onClick={handleDownloadMenuOpen}
            disabled={
              downloadStatus === "downloading" ||
              downloadStatus === "notavailable" ||
              data.length === 0
            }
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "40px",
              backgroundColor:
                downloadStatus === "completed" ? "green" : "#6d28d9",
              "&:hover": {
                backgroundColor:
                  downloadStatus === "completed" ? "darkgreen" : "#5b21b6",
              },
            }}
          >
            {downloadStatus === "downloading"
              ? "Downloading..."
              : downloadStatus === "completed"
              ? "Download Completed"
              : "Download Data"}
          </LoadingButton>
        </div>

      </div>
      <div className="flex items-center px-6 mt-4">

        <div className="flex items-center space-x-3">
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveReport}
            disabled={!checkIfSubmitValid() || calculationStatus !== "completed"}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "32px",
              borderColor: "#6d28d9",
              color: "#6d28d9",
              "&:hover": {
                borderColor: "#5b21b6",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            Save Report
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenSavedReportsDialog}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "32px",
              borderColor: "#6d28d9",
              color: "#6d28d9",
              "&:hover": {
                borderColor: "#5b21b6",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            View Saved {savedReportsLoading ? (
              <span className="inline-flex items-center">
                <svg className="animate-spin ml-1.5 h-3 w-3 text-violet-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            ) : (`(${savedReports.length})`)}
          </Button>
        </div>
      </div>

      <Menu
        anchorEl={downloadMenuAnchorEl}
        open={Boolean(downloadMenuAnchorEl)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => handleDownloadMenuSelect("detailed")}>
          {getDetailedExportLabel()}
        </MenuItem>
        <MenuItem onClick={() => handleDownloadMenuSelect("aggregated")}>
          Aggregate Time Period
        </MenuItem>
      </Menu>

      {calculationStatus === "completed" && (
        <div>
          {!compareLocationsChecked ? (
            <div className="mt-6 px-6 flex flex-wrap gap-x-8 gap-y-4">
              {enrichedSummaryData.map((metric, index) => (
                <div
                  key={index}
                  className="p-4 border border-slate-200 rounded-lg min-w-[180px]"
                >
                  <p className="text-xs text-slate-900">{metric.label}</p>
                  {metric.format === "dollars" ? (
                    <div className="mt-2 flex space-x-8">
                      {!metric.metric?.includes("splh") && (
                          <div>
                            <p className="text-xxs text-slate-500">Daily Avg</p>
                            <p className="text-lg text-slate-900">
                              {metric.average.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </p>
                          </div>
                        )}
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {metric.total.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </p>
                      </div>
                    </div>
                  ) : metric.format === "hours" ||
                    metric.format === "units" ||
                    metric.format === "number" ? (
                    <div className="mt-2 flex space-x-8">
                      {metric.metric !== "entrees_plh" && (
                        <div>
                          <p className="text-xxs text-slate-500">Daily Avg</p>
                          <p className="text-lg text-slate-900">
                            {metric.average.toFixed(2)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {metric.total.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : metric.format === "percentage" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {(metric.total * 100).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 px-6 flex flex-wrap gap-x-8 gap-y-4">
              {enrichedLocationData.map((location, index) => (
                <div
                  key={index}
                  className="p-4 border border-slate-200 rounded-lg min-w-[180px]"
                >
                  <p className="text-xs text-slate-900">{location.location}</p>
                  {location.format === "dollars" ? (
                    <div className="mt-2 flex space-x-8">
                      {!location.metric?.includes("splh") && (
                          <div>
                            <p className="text-xxs text-slate-500">Daily Avg</p>
                            <p className="text-lg text-slate-900">
                              {location.average.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </p>
                          </div>
                        )}
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {location.total.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </p>
                      </div>
                    </div>
                  ) : location.format === "hours" ||
                    location.format === "units" ||
                    location.format === "number" ? (
                    <div className="mt-2 flex space-x-8">
                      {location.metric !== "entrees_plh" && (
                        <div>
                          <p className="text-xxs text-slate-500">Daily Avg</p>
                          <p className="text-lg text-slate-900">
                            {location.average.toFixed(2)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {location.total.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : location.format === "percentage" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {(location.total * 100).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="min-h-[430px] h-100 mb-5 pt-6 xl:pt-2 pb-5 w-full">
        {!areMetricsSameFormat ? (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            <h3 className="text-center text-xl text-slate-500">
              Please select metrics of the same data type.
            </h3>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            {calculationStatus === "completed" ? (
              <h3 className="text-center text-xl text-slate-500">
                No data to show :(
              </h3>
            ) : checkIfSubmitValid() ? (
              <h3 className="text-center text-xl text-slate-500">
                Press Calculate Metric to see results.
              </h3>
            ) : (
              <h3 className="text-center text-xl text-slate-500">
                Select valid parameters and then press Calculate Metric to see
                results.
              </h3>
            )}
          </div>
        ) : data &&
          summaryData &&
          locationData &&
          calculationStatus === "completed" ? (
          <div>
            <WidgetRenderer
              widgetType={selectedWidgetType}
              activeTab={activeTab}
              intradayData={intradayData}
              data={data}
              rawData={rawData}
              selectedMetrics={calculatedMetrics}
              metrics={availableMetrics}
              compareLocations={compareLocationsChecked}
              compareDow={compareDow}
              summaryData={enrichedSummaryData}
              locationData={enrichedLocationData}
              isDashboard={false}
            />
          </div>
        ) : (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            {checkIfSubmitValid() ? (
              <h3 className="text-center text-xl text-slate-500">
                Press Calculate Metric to see results.
              </h3>
            ) : (
              <h3 className="text-center text-xl text-slate-500">
                Select valid parameters and then press Calculate Metric to see
                results.
              </h3>
            )}
          </div>
        )}
      </div>

      {/* Save Report Modal */}
      <Modal
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        aria-labelledby="save-report-modal"
        aria-describedby="save-report-description"
      >
        <Box sx={modalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setShowSaveDialog(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <SaveIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">Save Report</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-4">
                Enter a name for this report to save it for future use and add it to your dashboard.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Report Name
              </label>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a report name"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />

              {reportTabs.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Organize in Tab (Optional)
                  </label>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedTabForReport || ""}
                    onChange={(e) => setSelectedTabForReport(e.target.value || null)}
                    displayEmpty
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      padding: "5px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>No tab (All Reports)</em>
                    </MenuItem>
                    {reportTabs.map((tab) => (
                      <MenuItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReportConfirm}
                disabled={!reportName.trim() || saveStatus === "saving"}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                  ${reportName.trim() && saveStatus !== "saving"
                    ? 'bg-violet-700 text-white hover:bg-violet-800'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              >
                {saveStatus === "saving" ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Saved Reports Modal */}
      <SavedReportsModal
        open={showSavedReportsDialog}
        onClose={() => setShowSavedReportsDialog(false)}
        savedReports={savedReports}
        savedReportsLoading={savedReportsLoading}
        storeNames={props.storeNames}
        availableMetrics={availableMetrics}
        onDeleteReport={handleDeleteSavedReport}
        onReorderReports={handleReorderReports}
        onLoadReport={handleLoadSavedReport}
        showLoadButton={true}
      />
    </div>
  );
}

export default ReportingDisplay;
